import React from "react";
import { withRouter } from "react-router-dom";
import { Dropdown, Menu, Tooltip, Select, message, Spin, Button } from "antd";
import { HeartOutlined, MoreOutlined, ClockCircleOutlined, CheckCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import { REVIEW_IMAGE_CONTROLLER,CREATE_COLLABORATE_ENTITY,AI_IMAGE_GET_REJECTIONS } from "../../../../environments/env";
import "./ReviewImagesPage.scss";
import "../TwoDImagesPage/TwoDImages.scss";
import * as Utilities from "../../Utilities";

const ReviewAcceptedIcon = "/img/ai_creator/review_accepted.svg";
const ReviewRejectedIcon = "/img/ai_creator/review_rejected.svg";
const ReviewPendingIcon = "/img/ai_creator/review_pending.svg";

const { Option } = Select;

const getInitials = (name) => {
  if (!name) return '';

  const parts = name.trim().split(/[\s_]+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return parts.map(part => part.charAt(0).toUpperCase()).join('').substring(0, 2);
};

const ReviewImageCard = (props) => {
  const { image, onRemoveFromReview, currentTab, currentUserRole, onRefresh, history } = props;

  const canEditStatus = currentUserRole === 'admin' || currentUserRole === 'reviewer';
  const [isUpdating, setIsUpdating] = React.useState(false);

  const getTimeAgo = () => {
    const submittedTime = image.submitted_on || image.submitted_on_stamp;

    if (!submittedTime) return '0min';

    const now = new Date();
    const submitted = new Date(submittedTime);
    const diffMs = now - submitted;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays > 0) return `${diffDays}d`;
    if (diffHours > 0) return `${diffHours}h`;
    if (diffMins > 0) return `${diffMins}min`;
    return 'Just now';
  };

  const handleStatusChange = async (newStatus) => {
    setIsUpdating(true);
    try {
      const payload = {
        action: "update_status",
        image_id: image.id,
        done_by: localStorage.getItem('username') || 'unknown_user',
        status: newStatus
      };

      if(image.collab_id){
        payload.collab_id = image.collab_id;
      }

      console.log('Updating status with payload:', payload);

      const response = await axios.post(REVIEW_IMAGE_CONTROLLER, payload);

      if (response.data && response.data.statusCode === 200) {
        message.success(`Image status updated to ${newStatus}`);
        // Trigger refresh of parent component
        if (onRefresh) {
          onRefresh();
        }
      } else {
        message.error('Failed to update image status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('An error occurred while updating the status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCollaborate = async (image) => {
    console.log('Collaborate on image:', image);

    let prompt_text = image?.flux_prompt || image?.prompt_text;

    if(image.collab_id){
       window.location.href = `/collaborate-tool/${image.collab_id}?ai_image=true&productId=${image.product_id}&promptText=${prompt_text}&inspirationImageUri=${image.inspiration_image_uri}`;
    }
    else{
      setIsUpdating(true);
    
      const userName = localStorage.getItem('username')

      const payload = {
        image_id: image.id,
        display_name: image.title || "Untitled",
        camera_name: image.image_uri,
        thumbnail: image.image_uri.replace(/(\.[^.]+)$/, "_512$1"),
        created_by: userName,
        last_modified_by: userName,
        render_path: image.image_uri,
        state:{}
      }
      console.log('payload',payload)

      const res = await axios.post(CREATE_COLLABORATE_ENTITY, payload)

      console.log('res', res)
      window.location.href = `/collaborate-tool/${res.data.body.collaboration_entity_id}?ai_image=true&productId=${image.product_id}&promptText=${prompt_text}&inspirationImageUri=${image.inspiration_image_uri}`;
      setIsUpdating(false);
    }
  };

  const onViewRejectionHistory = async () => {
      // Navigate to rejection history page with image info
      history.push({
        pathname: '/rejection-history',
        search: `?imageId=${image.id}`,
        state: {
          imageId: image.id,
          imageTitle: image.title,
          createdBy: image.created_by,
          createdOn: image.submitted_on,
          collabId: image.collab_id
        }
      });
  }

  const getStatusOptions = () => {
    if (currentTab === 'pending') {
      return [
        { value: 'approved', label: 'Approve', icon: ReviewAcceptedIcon },
        { value: 'rejected', label: 'Reject', icon: ReviewRejectedIcon }
      ];
    } else if (currentTab === 'approved') {
      return [
        { value: 'pending', label: 'Move to pending', icon: ReviewPendingIcon },
        { value: 'rejected', label: 'Reject', icon: ReviewRejectedIcon }
      ];
    } else if (currentTab === 'rejected') {
      return [
        { value: 'pending', label: 'Move to pending', icon: ReviewPendingIcon },
        { value: 'approved', label: 'Approve', icon: ReviewAcceptedIcon }
      ];
    }
    return [];
  };

  const getStatusLabel = (status) => {
    if (status === 'pending') return 'Pending';
    if (status === 'approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    return status;
  };

  const getStatusIcon = (status) => {
    if (status === 'pending') return ReviewPendingIcon;
    if (status === 'approved') return ReviewAcceptedIcon;
    if (status === 'rejected') return ReviewRejectedIcon;
    return null;
  };

  const currentUserName = localStorage.getItem('username');
  const isImageCreator = currentUserName === image.created_by;

  const menu = (
    <Menu>
      <Menu.Item key="collaborate" onClick={() => {
            handleCollaborate(image);
          }
        }>
        Collaborate
      </Menu.Item>
      <Menu.Item key="rejection-history" onClick={() => onViewRejectionHistory(image)}>
        View Rejection History
      </Menu.Item>
      {image.submitted_for_review_by === localStorage.getItem('username') && (
        <Menu.Item key="remove" onClick={() => onRemoveFromReview(image)}>
          Remove from Review
        </Menu.Item>
      )}
    </Menu>
  );

  const getLowResImageUrl = (url) => {
    if (!url) return url;
    return url.replace(/(\.[^.]+)$/, "_512$1");
  };

  const getResolutionCategory = () => {
    if (image.width && image.height) {
      const maxDimension = Math.max(image.height, image.width);
      if (maxDimension <= 1024) return '1K';
      else if (maxDimension <= 2048) return '2K';
      else if (maxDimension <= 4096) return '4K';
      else return 'Custom';
    }
    return '';
  };

  const getFileFormat = () => {
    // Extract format from image_uri or use a default
    if (image.image_uri) {
      const extension = image.image_uri.split('.').pop().toUpperCase();
      return extension;
    }
    return 'JPG';
  };

  return (
    <div className={`review-image-card ${isUpdating ? 'updating' : ''}`}>
      <div className="card-image-container">
        {isUpdating && (
          <div className="card-loading-overlay">
            <Spin size="large" />
          </div>
        )}
        <img src={getLowResImageUrl(image.thumbnail)} alt={image.title} className="card-image" />

        {image.is_final && currentTab === 'pending' && (
          <div className="ai-overlay">
            <span className="ai-overlay-text">
              <CheckCircleOutlined style={{ marginRight: '10px' }} />
              Final Image
            </span>
          </div>
        )}

        <div className="card-top-right">
          {/* <HeartOutlined className="icon-button favorite-button" /> */}

          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <MoreOutlined className="icon-button more-button" />
          </Dropdown>
        </div>

        {/* Hover overlay with title and resolution */}
        <div className="hover-info">
          <div className="image-banner">
            <Tooltip
              title={
                <span className="manrope f-12 white">
                  {image.title}
                </span>
              }
            >
              <p className="manrope f-12 w-700 lh-140 clamp-text w-100 image-banner-text justify-in-start">
                {image.title}
              </p>
            </Tooltip>
            <p className="manrope f-12 image-banner-text">
              {image.width && image.height ? (
                <>
                  {image.width}x{image.height} {getResolutionCategory()} {getFileFormat()}
                </>
              ) : null}
            </p>
          </div>
        </div>
      </div>

      <div className="card-footer">
        <div className="card-top-row">
          <Tooltip title={image.created_by}>
            <div className="creator-badge">{getInitials(image.created_by)}</div>
          </Tooltip>
          <div className="time-indicator">
            <ClockCircleOutlined />
            <span>{Utilities.timestampToTimeSince(image.submitted_on_stamp)}</span>
          </div>
        </div>

        <div className={`card-bottom-row ${currentTab !== 'pending' || !canEditStatus ? 'row-layout' : ''}`}>
          <h3 className="card-title">{image.title}</h3>
          {canEditStatus ? (
            currentTab === 'pending' ? (
              <div className="justify-in-between card-status-button-container">
                {
                  getStatusOptions().map(option => (
                    <Button className="card-status-button" onClick={() => option.value === 'rejected' ? handleCollaborate(image) : handleStatusChange(option.value)}>
                      <img
                        src={option.icon}
                        alt=""
                        className="status-badge-icon"
                        style={{marginRight: '8px'}}
                      />
                      {option.label}
                    </Button>
                  ))
                }
              </div>
            ) : (
              <Select
                value={image.status}
                onChange={handleStatusChange}
                className="card-status-dropdown"
                dropdownMatchSelectWidth={false}
                disabled={isUpdating}
                suffixIcon={isUpdating ? <LoadingOutlined spin /> : undefined}
              >
                {getStatusOptions().map(option => (
                  <Option
                    key={option.value}
                    value={option.value}
                  >
                    <span className="status-option">
                      <img src={option.icon} alt="" className="status-icon" style={{ marginRight: '4px', width: '12px', height: '12px', verticalAlign: 'middle' }} />
                      {option.label}
                    </span>
                  </Option>
                ))}
                <Option
                  key={image.status}
                  value={image.status}
                  style={{ display: 'none' }}
                >
                  <span className="status-option">
                    <img src={getStatusIcon(image.status)} alt="" className="status-icon" style={{ marginRight: '4px', width: '12px', height: '12px', verticalAlign: 'middle' }} />
                    {getStatusLabel(image.status)}
                  </span>
                </Option>
              </Select>
            )
          ) : (
            <div className="card-status-badge">
              <img
                src={image.status === 'pending' ? ReviewPendingIcon : image.status === 'approved' ? ReviewAcceptedIcon : ReviewRejectedIcon}
                alt=""
                className="status-badge-icon"
              />
              {getStatusLabel(image.status)}
            </div>
          )}
        </div>

        <div className="card-meta">
          <span className="file-format">{image.file_format}</span>
        </div>
      </div>
    </div>
  );
};

export default withRouter(ReviewImageCard);
